import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Play, Pause, Square, SkipBack, SkipForward,
  Volume2, VolumeX, Plus, Trash2, Settings, Music, Layers,
  ChevronRight, ChevronDown, Folder, File, Mic, Headphones,
  Sliders, Grid, List, ZoomIn, ZoomOut, Save, Download,
  Copy, Scissors, RotateCcw, Redo, Eye, EyeOff, Lock, Unlock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as Tone from 'tone';

// ============================================================================
// PROFESSIONAL DAW-STYLE STUDIO
// Inspired by: Ableton Live, Reaper, Kontakt, Stylus RMX
// ============================================================================

// Note names for piano roll
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const OCTAVES = [1, 2, 3, 4, 5, 6, 7];
const ALL_NOTES = OCTAVES.flatMap(oct => NOTE_NAMES.map(note => `${note}${oct}`)).reverse();

// Instrument Library (CDN-hosted real samples)
const INSTRUMENT_LIBRARY = {
  // Strings
  cello: { name: 'Cello', category: 'Strings', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-cello-mp3@1.1.1/', samples: { 'A2': 'A2.mp3', 'A3': 'A3.mp3', 'A4': 'A4.mp3', 'C2': 'C2.mp3', 'C3': 'C3.mp3', 'C4': 'C4.mp3', 'C5': 'C5.mp3', 'E2': 'E2.mp3', 'E3': 'E3.mp3', 'E4': 'E4.mp3', 'G2': 'G2.mp3', 'G3': 'G3.mp3', 'G4': 'G4.mp3' }},
  violin: { name: 'Violin', category: 'Strings', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-violin-mp3@1.1.1/', samples: { 'A3': 'A3.mp3', 'A4': 'A4.mp3', 'A5': 'A5.mp3', 'C4': 'C4.mp3', 'C5': 'C5.mp3', 'C6': 'C6.mp3', 'E4': 'E4.mp3', 'E5': 'E5.mp3', 'G4': 'G4.mp3', 'G5': 'G5.mp3' }},
  contrabass: { name: 'Contrabass', category: 'Strings', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-contrabass-mp3@1.1.2/', samples: { 'A2': 'A2.mp3', 'C2': 'C2.mp3', 'E2': 'E2.mp3', 'G1': 'G1.mp3', 'D2': 'D2.mp3' }},
  harp: { name: 'Harp', category: 'Strings', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-harp-mp3@1.1.1/', samples: { 'A2': 'A2.mp3', 'A4': 'A4.mp3', 'C3': 'C3.mp3', 'C5': 'C5.mp3', 'E3': 'E3.mp3', 'E5': 'E5.mp3', 'G3': 'G3.mp3', 'G5': 'G5.mp3' }},
  
  // Brass
  trumpet: { name: 'Trumpet', category: 'Brass', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-trumpet-mp3@1.1.2/', samples: { 'A3': 'A3.mp3', 'A5': 'A5.mp3', 'C4': 'C4.mp3', 'C6': 'C6.mp3', 'E4': 'E4.mp3', 'G4': 'G4.mp3' }},
  tuba: { name: 'Tuba', category: 'Brass', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-tuba-mp3@1.1.2/', samples: { 'A#1': 'As1.mp3', 'A#2': 'As2.mp3', 'D2': 'D2.mp3', 'D3': 'D3.mp3', 'F1': 'F1.mp3', 'F2': 'F2.mp3' }},
  
  // Woodwinds
  flute: { name: 'Flute', category: 'Woodwinds', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-flute-mp3@1.1.2/', samples: { 'A4': 'A4.mp3', 'A5': 'A5.mp3', 'C4': 'C4.mp3', 'C5': 'C5.mp3', 'C6': 'C6.mp3', 'E4': 'E4.mp3', 'E5': 'E5.mp3' }},
  saxophone: { name: 'Saxophone', category: 'Woodwinds', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-saxophone-mp3@1.1.2/', samples: { 'A3': 'A3.mp3', 'A4': 'A4.mp3', 'C3': 'C3.mp3', 'C4': 'C4.mp3', 'E3': 'E3.mp3', 'E4': 'E4.mp3', 'G3': 'G3.mp3', 'G4': 'G4.mp3' }},
  bassoon: { name: 'Bassoon', category: 'Woodwinds', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-bassoon-mp3@1.1.2/', samples: { 'A2': 'A2.mp3', 'A3': 'A3.mp3', 'C3': 'C3.mp3', 'C4': 'C4.mp3', 'E3': 'E3.mp3', 'G2': 'G2.mp3', 'G3': 'G3.mp3' }},
  
  // Keys
  piano: { name: 'Grand Piano', category: 'Keys', cdnBase: 'https://tonejs.github.io/audio/salamander/', samples: { 'A0': 'A0.mp3', 'C1': 'C1.mp3', 'A1': 'A1.mp3', 'C2': 'C2.mp3', 'A2': 'A2.mp3', 'C3': 'C3.mp3', 'A3': 'A3.mp3', 'C4': 'C4.mp3', 'A4': 'A4.mp3', 'C5': 'C5.mp3', 'A5': 'A5.mp3', 'C6': 'C6.mp3', 'A6': 'A6.mp3', 'C7': 'C7.mp3', 'C8': 'C8.mp3' }},
  organ: { name: 'Organ', category: 'Keys', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-organ-mp3@1.1.1/', samples: { 'A2': 'A2.mp3', 'A3': 'A3.mp3', 'A4': 'A4.mp3', 'C3': 'C3.mp3', 'C4': 'C4.mp3', 'C5': 'C5.mp3' }},
  
  // Guitar & Bass
  'guitar-acoustic': { name: 'Acoustic Guitar', category: 'Guitar', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-guitar-acoustic-mp3@1.1.2/', samples: { 'A2': 'A2.mp3', 'A3': 'A3.mp3', 'C3': 'C3.mp3', 'C4': 'C4.mp3', 'E2': 'E2.mp3', 'E3': 'E3.mp3', 'E4': 'E4.mp3', 'G2': 'G2.mp3', 'G3': 'G3.mp3' }},
  'bass-electric': { name: 'Electric Bass', category: 'Bass', cdnBase: 'https://cdn.jsdelivr.net/npm/tonejs-instrument-bass-electric-mp3@1.1.2/', samples: { 'A1': 'A1.mp3', 'A2': 'A2.mp3', 'C2': 'C2.mp3', 'C3': 'C3.mp3', 'E1': 'E1.mp3', 'E2': 'E2.mp3', 'G1': 'G1.mp3', 'G2': 'G2.mp3' }},
};

// Group instruments by category
const INSTRUMENT_CATEGORIES = Object.entries(INSTRUMENT_LIBRARY).reduce((acc, [id, inst]) => {
  if (!acc[inst.category]) acc[inst.category] = [];
  acc[inst.category].push({ id, ...inst });
  return acc;
}, {});

// ============================================================================
// TRANSPORT BAR (Top - Play/Stop/Record/Tempo)
// ============================================================================
const TransportBar = ({ 
  isPlaying, onPlayPause, onStop, onRewind,
  tempo, onTempoChange,
  timeSignature, onTimeSignatureChange,
  position, projectName
}) => {
  return (
    <div className="h-14 bg-[#1a1a1a] border-b border-[#333] flex items-center px-4 gap-6">
      {/* Back Button */}
      <Link to="/admin" className="p-2 hover:bg-white/10 rounded-lg transition-colors">
        <ArrowLeft className="w-5 h-5 text-gray-400" />
      </Link>
      
      {/* Project Name */}
      <div className="flex-1 max-w-xs">
        <input
          type="text"
          value={projectName}
          className="bg-transparent text-white font-medium text-lg w-full focus:outline-none focus:bg-white/5 rounded px-2 py-1"
          placeholder="Untitled Project"
        />
      </div>
      
      {/* Transport Controls */}
      <div className="flex items-center gap-1 bg-[#252525] rounded-lg p-1">
        <button
          onClick={onRewind}
          className="p-2 hover:bg-white/10 rounded transition-colors"
          title="Rewind"
        >
          <SkipBack className="w-4 h-4 text-gray-300" />
        </button>
        <button
          onClick={onStop}
          className="p-2 hover:bg-white/10 rounded transition-colors"
          title="Stop"
        >
          <Square className="w-4 h-4 text-gray-300" />
        </button>
        <button
          onClick={onPlayPause}
          className={`p-2 rounded transition-colors ${isPlaying ? 'bg-green-500 text-white' : 'hover:bg-white/10 text-gray-300'}`}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button
          className="p-2 hover:bg-white/10 rounded transition-colors"
          title="Record"
        >
          <div className="w-4 h-4 rounded-full bg-red-500" />
        </button>
      </div>
      
      {/* Position Display */}
      <div className="bg-[#0a0a0a] px-4 py-2 rounded font-mono text-green-400 text-sm">
        {position || '1.1.1'}
      </div>
      
      {/* Tempo */}
      <div className="flex items-center gap-2">
        <span className="text-gray-400 text-sm">BPM</span>
        <input
          type="number"
          value={tempo}
          onChange={(e) => onTempoChange(parseInt(e.target.value) || 120)}
          className="w-16 bg-[#0a0a0a] text-white text-center rounded px-2 py-1 font-mono"
          min="20"
          max="300"
        />
      </div>
      
      {/* Time Signature */}
      <div className="flex items-center gap-2">
        <select
          value={timeSignature}
          onChange={(e) => onTimeSignatureChange(e.target.value)}
          className="bg-[#0a0a0a] text-white rounded px-2 py-1 text-sm"
        >
          <option value="4/4">4/4</option>
          <option value="3/4">3/4</option>
          <option value="2/4">2/4</option>
          <option value="6/8">6/8</option>
        </select>
      </div>
      
      {/* Save/Export */}
      <div className="flex items-center gap-2 ml-auto">
        <button className="px-3 py-1.5 bg-[#252525] hover:bg-[#333] text-gray-300 rounded text-sm flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save
        </button>
        <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// INSTRUMENT BROWSER (Left Panel - Kontakt/Stylus RMX style)
// ============================================================================
const InstrumentBrowser = ({ 
  onSelectInstrument, 
  loadedInstruments,
  loadingInstrument,
  userSamples 
}) => {
  const [expandedCategories, setExpandedCategories] = useState(['Keys', 'Strings']);
  const [searchQuery, setSearchQuery] = useState('');
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };
  
  const filteredCategories = Object.entries(INSTRUMENT_CATEGORIES).filter(([category, instruments]) =>
    category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    instruments.some(inst => inst.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  return (
    <div className="w-64 bg-[#1e1e1e] border-r border-[#333] flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-[#333]">
        <h2 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
          <Layers className="w-4 h-4" />
          Instruments
        </h2>
        <input
          type="text"
          placeholder="Search instruments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-[#0a0a0a] text-white text-sm rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      
      {/* Instrument List */}
      <div className="flex-1 overflow-y-auto p-2">
        {filteredCategories.map(([category, instruments]) => (
          <div key={category} className="mb-2">
            <button
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-300 hover:bg-white/5 rounded text-sm font-medium"
            >
              {expandedCategories.includes(category) ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <Folder className="w-4 h-4 text-yellow-500" />
              {category}
              <span className="ml-auto text-gray-500 text-xs">{instruments.length}</span>
            </button>
            
            {expandedCategories.includes(category) && (
              <div className="ml-4 mt-1 space-y-0.5">
                {instruments.map(inst => (
                  <button
                    key={inst.id}
                    onClick={() => onSelectInstrument(inst.id)}
                    disabled={loadingInstrument === inst.id}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${
                      loadedInstruments[inst.id] 
                        ? 'text-green-400 bg-green-500/10' 
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    } ${loadingInstrument === inst.id ? 'opacity-50' : ''}`}
                  >
                    <Music className="w-3 h-3" />
                    {inst.name}
                    {loadedInstruments[inst.id] && (
                      <span className="ml-auto text-xs text-green-500">✓</span>
                    )}
                    {loadingInstrument === inst.id && (
                      <span className="ml-auto text-xs text-blue-400">...</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        
        {/* User Samples Section */}
        {Object.keys(userSamples || {}).length > 0 && (
          <div className="mb-2 mt-4 pt-4 border-t border-[#333]">
            <button
              onClick={() => toggleCategory('My Samples')}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-300 hover:bg-white/5 rounded text-sm font-medium"
            >
              {expandedCategories.includes('My Samples') ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <Folder className="w-4 h-4 text-orange-500" />
              My Samples
            </button>
            
            {expandedCategories.includes('My Samples') && (
              <div className="ml-4 mt-1 space-y-0.5">
                {Object.entries(userSamples).map(([id, sample]) => (
                  <button
                    key={id}
                    onClick={() => onSelectInstrument(`user:${id}`)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-gray-400 hover:bg-white/5 hover:text-white rounded text-sm"
                  >
                    <File className="w-3 h-3" />
                    {id}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t border-[#333]">
        <Link
          to="/admin/sample-manager"
          className="w-full py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded text-sm font-medium flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Import Samples
        </Link>
      </div>
    </div>
  );
};

// ============================================================================
// TRACK CONTROL PANEL (Reaper-style - Left of Piano Roll)
// ============================================================================
const TrackPanel = ({ 
  tracks, 
  selectedTrackId,
  onSelectTrack, 
  onAddTrack, 
  onDeleteTrack,
  onMuteTrack,
  onSoloTrack,
  onVolumeChange,
  onPanChange 
}) => {
  return (
    <div className="w-48 bg-[#1a1a1a] border-r border-[#333] flex flex-col">
      {/* Header */}
      <div className="p-2 border-b border-[#333] flex items-center justify-between">
        <span className="text-gray-400 text-xs font-medium uppercase">Tracks</span>
        <button
          onClick={onAddTrack}
          className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
          title="Add Track"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      
      {/* Track List */}
      <div className="flex-1 overflow-y-auto">
        {tracks.map((track, index) => (
          <div
            key={track.id}
            onClick={() => onSelectTrack(track.id)}
            className={`border-b border-[#333] cursor-pointer transition-colors ${
              selectedTrackId === track.id 
                ? 'bg-blue-500/20 border-l-2 border-l-blue-500' 
                : 'hover:bg-white/5'
            }`}
          >
            {/* Track Header */}
            <div className="px-2 py-1.5 flex items-center gap-2">
              <span className="text-gray-500 text-xs w-4">{index + 1}</span>
              <input
                type="text"
                value={track.name}
                onChange={(e) => {/* handle rename */}}
                className="flex-1 bg-transparent text-white text-sm font-medium focus:outline-none truncate"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteTrack(track.id); }}
                className="p-1 hover:bg-red-500/20 rounded text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            
            {/* Track Controls */}
            <div className="px-2 pb-2 flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); onMuteTrack(track.id); }}
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  track.muted ? 'bg-red-500 text-white' : 'bg-[#333] text-gray-400 hover:text-white'
                }`}
              >
                M
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onSoloTrack(track.id); }}
                className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                  track.solo ? 'bg-yellow-500 text-black' : 'bg-[#333] text-gray-400 hover:text-white'
                }`}
              >
                S
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={track.volume}
                onChange={(e) => onVolumeChange(track.id, parseFloat(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 h-1 accent-blue-500"
              />
            </div>
            
            {/* Instrument Badge */}
            <div className="px-2 pb-1.5">
              <span className="text-xs px-1.5 py-0.5 bg-[#333] text-gray-400 rounded">
                {track.instrumentName || 'No Instrument'}
              </span>
            </div>
          </div>
        ))}
        
        {tracks.length === 0 && (
          <div className="p-4 text-center text-gray-500 text-sm">
            No tracks yet.<br />
            Click + to add a track.
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PIANO ROLL (Main Editor - Ableton/FL Studio style)
// ============================================================================
const PianoRollEditor = ({
  track,
  measures,
  timeSignature,
  zoom,
  onZoomChange,
  onNotesChange,
  selectedTool,
  onToolChange,
  snapValue,
  onSnapChange,
  sampler
}) => {
  const gridRef = useRef(null);
  const [notes, setNotes] = useState(track?.notes || []);
  const [isDragging, setIsDragging] = useState(false);
  const [dragNote, setDragNote] = useState(null);
  
  const cellWidth = 20 * zoom;
  const cellHeight = 14;
  const keyboardWidth = 48;
  const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
  const subdivisions = 4; // 16th notes
  const totalCells = measures * beatsPerMeasure * subdivisions;
  
  useEffect(() => {
    if (track?.notes) {
      setNotes(track.notes);
    }
  }, [track]);
  
  // Play note preview
  const playNotePreview = async (noteName) => {
    try {
      await Tone.start();
      if (sampler && sampler.loaded) {
        sampler.triggerAttackRelease(noteName, '8n');
      }
    } catch (e) {
      console.warn('Preview error:', e);
    }
  };
  
  // Handle grid click
  const handleGridClick = (e) => {
    if (!gridRef.current) return;
    
    const rect = gridRef.current.getBoundingClientRect();
    const scrollLeft = gridRef.current.scrollLeft;
    const scrollTop = gridRef.current.scrollTop;
    const x = e.clientX - rect.left + scrollLeft - keyboardWidth;
    const y = e.clientY - rect.top + scrollTop;
    
    if (x < 0) return; // Clicked on keyboard
    
    const cellX = Math.floor(x / cellWidth);
    const cellY = Math.floor(y / cellHeight);
    
    if (cellY < 0 || cellY >= ALL_NOTES.length) return;
    
    const noteName = ALL_NOTES[cellY];
    const beat = cellX / subdivisions;
    const measure = Math.floor(beat / beatsPerMeasure);
    const beatInMeasure = beat % beatsPerMeasure;
    
    if (selectedTool === 'draw') {
      // Check if note exists at this position
      const existingIndex = notes.findIndex(n => {
        const nBeat = n.measure * beatsPerMeasure + (n.beat || 0);
        const nName = n.pitch.replace('/', '');
        return Math.abs(nBeat - beat) < 0.1 && nName === noteName;
      });
      
      if (existingIndex >= 0) {
        // Remove note
        const newNotes = notes.filter((_, i) => i !== existingIndex);
        setNotes(newNotes);
        onNotesChange(newNotes);
      } else {
        // Add note
        const newNote = {
          measure,
          beat: beatInMeasure,
          pitch: noteName.replace(/(\d)/, '/$1'),
          duration: 'q',
          velocity: 0.8
        };
        const newNotes = [...notes, newNote];
        setNotes(newNotes);
        onNotesChange(newNotes);
        playNotePreview(noteName);
      }
    } else if (selectedTool === 'erase') {
      const newNotes = notes.filter(n => {
        const nBeat = n.measure * beatsPerMeasure + (n.beat || 0);
        const nName = n.pitch.replace('/', '');
        return !(Math.abs(nBeat - beat) < 0.1 && nName === noteName);
      });
      setNotes(newNotes);
      onNotesChange(newNotes);
    }
  };
  
  const isBlackKey = (note) => note.includes('#');
  
  if (!track) {
    return (
      <div className="flex-1 bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center text-gray-500">
          <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Select a track to edit</p>
          <p className="text-sm mt-1">or add a new track from the left panel</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 bg-[#1a1a1a] flex flex-col">
      {/* Toolbar */}
      <div className="h-10 bg-[#252525] border-b border-[#333] flex items-center px-3 gap-4">
        {/* Tools */}
        <div className="flex items-center gap-1 bg-[#1a1a1a] rounded p-0.5">
          <button
            onClick={() => onToolChange('select')}
            className={`p-1.5 rounded ${selectedTool === 'select' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Select"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
          </button>
          <button
            onClick={() => onToolChange('draw')}
            className={`p-1.5 rounded ${selectedTool === 'draw' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Draw"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            onClick={() => onToolChange('erase')}
            className={`p-1.5 rounded ${selectedTool === 'erase' ? 'bg-red-500 text-white' : 'text-gray-400 hover:text-white'}`}
            title="Erase"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        
        {/* Snap */}
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs">Snap:</span>
          <select
            value={snapValue}
            onChange={(e) => onSnapChange(e.target.value)}
            className="bg-[#1a1a1a] text-white text-xs rounded px-2 py-1"
          >
            <option value="1/4">1/4</option>
            <option value="1/8">1/8</option>
            <option value="1/16">1/16</option>
            <option value="1/32">1/32</option>
            <option value="off">Off</option>
          </select>
        </div>
        
        {/* Zoom */}
        <div className="flex items-center gap-1 ml-auto">
          <button
            onClick={() => onZoomChange(Math.max(0.5, zoom - 0.25))}
            className="p-1 text-gray-400 hover:text-white"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-gray-400 text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={() => onZoomChange(Math.min(3, zoom + 0.25))}
            className="p-1 text-gray-400 hover:text-white"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
        
        {/* Track Info */}
        <div className="text-sm text-gray-400">
          {track.name} • {notes.length} notes
        </div>
      </div>
      
      {/* Piano Roll Grid */}
      <div 
        ref={gridRef}
        className="flex-1 overflow-auto relative"
        style={{ cursor: selectedTool === 'draw' ? 'crosshair' : selectedTool === 'erase' ? 'not-allowed' : 'default' }}
      >
        <div 
          className="flex relative"
          style={{ minWidth: keyboardWidth + totalCells * cellWidth, minHeight: ALL_NOTES.length * cellHeight }}
        >
          {/* Piano Keyboard */}
          <div 
            className="sticky left-0 z-20 bg-[#1e1e1e]"
            style={{ width: keyboardWidth }}
          >
            {ALL_NOTES.map((note, i) => (
              <div
                key={note}
                onClick={() => playNotePreview(note)}
                className={`flex items-center justify-end pr-1 text-[10px] font-mono cursor-pointer border-b border-[#333] ${
                  isBlackKey(note)
                    ? 'bg-[#333] text-gray-400'
                    : 'bg-[#555] text-white'
                } ${note.startsWith('C') && !note.includes('#') ? 'border-b-blue-500' : ''}`}
                style={{ height: cellHeight }}
              >
                {note}
              </div>
            ))}
          </div>
          
          {/* Grid */}
          <div 
            className="relative flex-1"
            onClick={handleGridClick}
          >
            {/* Grid Lines */}
            <div className="absolute inset-0">
              {/* Horizontal lines */}
              {ALL_NOTES.map((note, i) => (
                <div
                  key={`row-${i}`}
                  className={`absolute left-0 right-0 border-b ${
                    isBlackKey(note) ? 'bg-[#1a1a1a]' : 'bg-[#222]'
                  } ${note.startsWith('C') && !note.includes('#') ? 'border-b-[#444]' : 'border-b-[#2a2a2a]'}`}
                  style={{ top: i * cellHeight, height: cellHeight }}
                />
              ))}
              
              {/* Vertical lines - beats and measures */}
              {Array.from({ length: totalCells + 1 }).map((_, i) => {
                const isMeasure = i % (beatsPerMeasure * subdivisions) === 0;
                const isBeat = i % subdivisions === 0;
                return (
                  <div
                    key={`col-${i}`}
                    className={`absolute top-0 bottom-0 ${
                      isMeasure ? 'border-l border-[#555]' :
                      isBeat ? 'border-l border-[#3a3a3a]' :
                      'border-l border-[#2a2a2a]'
                    }`}
                    style={{ left: i * cellWidth }}
                  />
                );
              })}
            </div>
            
            {/* Notes */}
            {notes.map((note, i) => {
              const noteName = note.pitch.replace('/', '');
              const rowIndex = ALL_NOTES.indexOf(noteName);
              const beat = note.measure * beatsPerMeasure + (note.beat || 0);
              const cellX = beat * subdivisions;
              const durationBeats = note.duration === 'w' ? 4 : note.duration === 'h' ? 2 : note.duration === 'q' ? 1 : note.duration === '8' ? 0.5 : 0.25;
              const width = durationBeats * subdivisions * cellWidth - 2;
              
              if (rowIndex < 0) return null;
              
              return (
                <motion.div
                  key={i}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute bg-blue-500 rounded-sm border border-blue-400 cursor-pointer hover:bg-blue-400"
                  style={{
                    left: cellX * cellWidth + 1,
                    top: rowIndex * cellHeight + 1,
                    width: Math.max(width, cellWidth * subdivisions - 2),
                    height: cellHeight - 2,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (selectedTool === 'erase' || selectedTool === 'draw') {
                      const newNotes = notes.filter((_, idx) => idx !== i);
                      setNotes(newNotes);
                      onNotesChange(newNotes);
                    } else {
                      playNotePreview(noteName);
                    }
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN STUDIO COMPONENT
// ============================================================================
const StudioDAW = () => {
  // Project state
  const [projectName, setProjectName] = useState('New Project');
  const [tempo, setTempo] = useState(120);
  const [timeSignature, setTimeSignature] = useState('4/4');
  const [measures, setMeasures] = useState(16);
  const [isPlaying, setIsPlaying] = useState(false);
  const [position, setPosition] = useState('1.1.1');
  
  // Tracks
  const [tracks, setTracks] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  
  // Editor state
  const [selectedTool, setSelectedTool] = useState('draw');
  const [zoom, setZoom] = useState(1);
  const [snapValue, setSnapValue] = useState('1/16');
  
  // Instruments
  const [loadedInstruments, setLoadedInstruments] = useState({});
  const [loadingInstrument, setLoadingInstrument] = useState(null);
  const samplersRef = useRef({});
  
  // User samples
  const [userSamples, setUserSamples] = useState({});
  
  // Get selected track
  const selectedTrack = tracks.find(t => t.id === selectedTrackId);
  
  // Load instrument
  const loadInstrument = useCallback(async (instrumentId) => {
    if (samplersRef.current[instrumentId]) {
      return samplersRef.current[instrumentId];
    }
    
    const lib = INSTRUMENT_LIBRARY[instrumentId];
    if (!lib) return null;
    
    setLoadingInstrument(instrumentId);
    
    return new Promise((resolve) => {
      const urls = {};
      for (const [note, file] of Object.entries(lib.samples)) {
        urls[note] = lib.cdnBase + file;
      }
      
      const sampler = new Tone.Sampler(urls, {
        release: 1,
        onload: () => {
          console.log(`✓ ${lib.name} loaded`);
          samplersRef.current[instrumentId] = sampler;
          setLoadedInstruments(prev => ({ ...prev, [instrumentId]: true }));
          setLoadingInstrument(null);
          resolve(sampler);
        },
        onerror: (err) => {
          console.error(`Failed to load ${lib.name}:`, err);
          setLoadingInstrument(null);
          resolve(null);
        }
      }).toDestination();
      sampler.volume.value = -6;
    });
  }, []);
  
  // Add track
  const addTrack = async (instrumentId = 'piano') => {
    const sampler = await loadInstrument(instrumentId);
    const lib = INSTRUMENT_LIBRARY[instrumentId];
    
    const newTrack = {
      id: `track_${Date.now()}`,
      name: lib?.name || 'New Track',
      instrumentId,
      instrumentName: lib?.name || instrumentId,
      notes: [],
      volume: 0.8,
      pan: 0,
      muted: false,
      solo: false
    };
    
    setTracks(prev => [...prev, newTrack]);
    setSelectedTrackId(newTrack.id);
    toast.success(`Added ${lib?.name || 'track'}`);
  };
  
  // Delete track
  const deleteTrack = (trackId) => {
    setTracks(prev => prev.filter(t => t.id !== trackId));
    if (selectedTrackId === trackId) {
      setSelectedTrackId(tracks.length > 1 ? tracks[0].id : null);
    }
  };
  
  // Update track notes
  const updateTrackNotes = (notes) => {
    setTracks(prev => prev.map(t => 
      t.id === selectedTrackId ? { ...t, notes } : t
    ));
  };
  
  // Mute/Solo
  const muteTrack = (trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, muted: !t.muted } : t
    ));
  };
  
  const soloTrack = (trackId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, solo: !t.solo } : t
    ));
  };
  
  // Volume change
  const changeVolume = (trackId, volume) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, volume } : t
    ));
  };
  
  // Play/Pause
  const togglePlay = async () => {
    if (isPlaying) {
      Tone.Transport.stop();
      Tone.Transport.cancel();
      setIsPlaying(false);
      return;
    }
    
    await Tone.start();
    Tone.Transport.bpm.value = tempo;
    Tone.Transport.cancel();
    
    const beatsPerMeasure = parseInt(timeSignature.split('/')[0]);
    
    // Schedule all notes
    tracks.filter(t => !t.muted).forEach(track => {
      const sampler = samplersRef.current[track.instrumentId];
      if (!sampler || !sampler.loaded) return;
      
      track.notes.forEach(note => {
        const beat = note.measure * beatsPerMeasure + (note.beat || 0);
        
        Tone.Transport.schedule((t) => {
          const pitch = note.pitch.replace('/', '');
          const duration = note.duration === 'w' ? '1n' : note.duration === 'h' ? '2n' : note.duration === 'q' ? '4n' : note.duration === '8' ? '8n' : '16n';
          sampler.triggerAttackRelease(pitch, duration, t, track.volume);
        }, `0:${beat}`);
      });
    });
    
    Tone.Transport.start();
    setIsPlaying(true);
  };
  
  // Stop
  const stop = () => {
    Tone.Transport.stop();
    Tone.Transport.cancel();
    setIsPlaying(false);
    setPosition('1.1.1');
  };
  
  // Rewind
  const rewind = () => {
    Tone.Transport.position = 0;
    setPosition('1.1.1');
  };
  
  // Select instrument from browser
  const handleSelectInstrument = async (instrumentId) => {
    if (selectedTrackId) {
      // Change instrument for selected track
      const sampler = await loadInstrument(instrumentId);
      const lib = INSTRUMENT_LIBRARY[instrumentId];
      
      setTracks(prev => prev.map(t => 
        t.id === selectedTrackId 
          ? { ...t, instrumentId, instrumentName: lib?.name || instrumentId }
          : t
      ));
      toast.success(`Changed to ${lib?.name}`);
    } else {
      // Create new track with this instrument
      addTrack(instrumentId);
    }
  };
  
  // Load piano on mount
  useEffect(() => {
    loadInstrument('piano');
  }, [loadInstrument]);
  
  return (
    <div className="h-screen flex flex-col bg-[#121212] text-white overflow-hidden">
      {/* Transport Bar */}
      <TransportBar
        isPlaying={isPlaying}
        onPlayPause={togglePlay}
        onStop={stop}
        onRewind={rewind}
        tempo={tempo}
        onTempoChange={setTempo}
        timeSignature={timeSignature}
        onTimeSignatureChange={setTimeSignature}
        position={position}
        projectName={projectName}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Instrument Browser */}
        <InstrumentBrowser
          onSelectInstrument={handleSelectInstrument}
          loadedInstruments={loadedInstruments}
          loadingInstrument={loadingInstrument}
          userSamples={userSamples}
        />
        
        {/* Track Panel */}
        <TrackPanel
          tracks={tracks}
          selectedTrackId={selectedTrackId}
          onSelectTrack={setSelectedTrackId}
          onAddTrack={() => addTrack('piano')}
          onDeleteTrack={deleteTrack}
          onMuteTrack={muteTrack}
          onSoloTrack={soloTrack}
          onVolumeChange={changeVolume}
        />
        
        {/* Piano Roll Editor */}
        <PianoRollEditor
          track={selectedTrack}
          measures={measures}
          timeSignature={timeSignature}
          zoom={zoom}
          onZoomChange={setZoom}
          onNotesChange={updateTrackNotes}
          selectedTool={selectedTool}
          onToolChange={setSelectedTool}
          snapValue={snapValue}
          onSnapChange={setSnapValue}
          sampler={selectedTrack ? samplersRef.current[selectedTrack.instrumentId] : null}
        />
      </div>
    </div>
  );
};

export default StudioDAW;
